// This is the main dashboard page
// It shows a high level overview of all devices and driver status for an organization

import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import DeviceStatusIcon from "../components/DeviceStatusIcon";
import useSuspended from "../hooks/useSuspended";
import DeployToGroupModal from "../components/DeployToGroupModal";
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";

// The address of our backend server
const API_URL = config.API_URL;

export default function Dashboard() {
  const isSuspended = useSuspended();
  // Get the logged in user's organization ID from sessionStorage
  // This ensures each user only sees their own company's data
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;
  const navigate = useNavigate();

  // State variables store data that the page needs to display
  const [summary, setSummary] = useState(null);   // the org summary from our API
  const [loading, setLoading] = useState(true);   // whether we are still fetching data
  const [error, setError] = useState(null);       // any error that occurred
  const [history, setHistory] = useState([]);   // stores historical scan data for the line chart
  const [dashboardDevices, setDashboardDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);  // tracks which devices are checked
  const [pendingRequests, setPendingRequests] = useState(0);
  const [referenceDevices, setReferenceDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [twPackages, setTwPackages] = useState([]);
  const [showSaveGroupModal, setShowSaveGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  

  useEffect(() => {
    // Fetch organization summary data
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/summary`)
      .then((res) => {
        setSummary(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load dashboard data");
        setLoading(false);
      });

    // Fetch historical data for the line chart
    // Groups driver scans by date to show trends over 30 days
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/history`)
      .then((res) => {
        // Backend now returns daily device compliance summaries
        const sorted = res.data
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-30)
          .map(d => ({
            date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            compliant: d.compliant,
            compliant_stale: d.compliant_stale || 0,
            non_compliant: d.non_compliant,
            unknown: d.unknown
          }));
        setHistory(sorted);
      })
      .catch(() => {
        // If history fails just show empty chart
        setHistory([]);
      });
      
      // Fetch pending access requests count for navbar badge
      axios
        .get(`${API_URL}/organizations/${ORG_ID}/access-requests`)
        .then((res) => setPendingRequests(res.data.length))
        .catch(() => setPendingRequests(0));

      // Fetch reference devices
      axios
        .get(`${API_URL}/organizations/${ORG_ID}/reference-devices`)
        .then((res) => setReferenceDevices(res.data))
        .catch(() => setReferenceDevices([]));

      // Fetch TriggerWatch packages for the Deploy to Selected modal
      axios
        .get(`${API_URL}/organizations/${ORG_ID}/tw-packages`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        })
        .then((res) => setTwPackages(res.data))
        .catch(() => setTwPackages([]));

      // Fetch devices list with compliance data
    // Fetch devices list with compliance data
    axios
      .get(`${API_URL}/organizations/${ORG_ID}/devices`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      })
      .then((res) => {
        setDashboardDevices(res.data);
      })
      .catch(() => {
        setDashboardDevices([]);
      });
  }, []);

  // Show a loading message while data is being fetched
  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-white text-xl">Loading dashboard...</p>
    </div>
  );

  // Show an error message if something went wrong
  if (error) return (
    <div className="flex items-center justify-center h-screen bg-gray-950">
      <p className="text-red-400 text-xl">{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <Navbar orgName={summary?.organization} pendingRequests={pendingRequests} />

      {/* Main content area */}
      <main className="px-8 py-8">

        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Overview of your device and driver health</p>
        </div>

        {/* Charts row - Pie chart and Historical line chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          {/* Pie Chart - Driver Status Breakdown */}
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <h2 className="text-white font-semibold mb-4">Device Compliance Overview</h2>
            {summary && (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Compliant", value: summary.compliant_devices || 0, status: "up_to_date" },
                      { name: "Partial", value: summary.partial_devices || 0, status: "outdated" },
                      { name: "Non-Compliant", value: summary.noncompliant_devices || 0, status: "outdated" },
                      { name: "No Data", value: summary.nodata_devices || 0, status: "unknown" }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={true}
                    onClick={(data) => navigate(`/compliance/${data.status}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <Cell fill="#22C55E" />
                    <Cell fill="#EAB308" />
                    <Cell fill="#EF4444" />
                    <Cell fill="#6B7280" />
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1F2937", border: "1px solid #374151", borderRadius: "8px" }}
                    labelStyle={{ color: "#F9FAFB" }}
                    itemStyle={{ color: "#D1D5DB" }}
                  />
                  <Legend
                    wrapperStyle={{ color: "#9CA3AF", fontSize: "12px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Line Chart - Historical 30 day trend */}
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <h2 className="text-white font-semibold mb-4">30 Day Device Compliance Trend</h2>
            {history.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#9CA3AF", fontSize: 11 }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#9CA3AF", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1F2937", border: "1px solid #374151", borderRadius: "8px" }}
                    labelStyle={{ color: "#F9FAFB" }}
                    itemStyle={{ color: "#D1D5DB" }}
                  />
                  <Legend
                    wrapperStyle={{ color: "#9CA3AF", fontSize: "12px" }}
                  />
                  <Bar dataKey="non_compliant" fill="#EF4444" name="Non-Compliant Devices" stackId="a" onClick={() => navigate("/compliance/outdated")} style={{ cursor: "pointer" }} />
                  <Bar dataKey="unknown" fill="#6B7280" name="No Data" stackId="a" onClick={() => navigate("/compliance/unknown")} style={{ cursor: "pointer" }} />
                  <Bar dataKey="compliant_stale" fill="#86EFAC" name="Compliant (Not Reporting)" stackId="a" onClick={() => navigate("/compliance/up_to_date")} style={{ cursor: "pointer" }} />
                  <Bar dataKey="compliant" fill="#22C55E" name="Compliant Devices" stackId="a" radius={[4, 4, 0, 0]} onClick={() => navigate("/compliance/up_to_date")} style={{ cursor: "pointer" }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500 text-sm">No historical data yet — run more scans to see trends</p>
              </div>
            )}
          </div>

        </div>

        {/* Summary cards row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">

          {/* Total Devices card */}
          <div
           onClick={() => navigate("/compliance/all")}
          className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Total Devices</p>
            <p className="text-3xl font-bold text-white">{summary?.total_devices}</p>
            <p className="text-gray-500 text-xs mt-2">Reporting in</p>
          </div>

          {/* Total Model Types card */}
          <div
          onClick={() => navigate("/compliance/all")}
          className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <p className="text-gray-400 text-sm mb-1">Model Types</p>
            <p className="text-3xl font-bold text-white">{summary?.total_models}</p>
            <p className="text-gray-500 text-xs mt-2">Unique device models</p>
          </div>

          {/* Outdated Devices card */}
          <div
          onClick={() => navigate("/compliance/outdated")}
          className="bg-gray-900 rounded-xl p-6 border border-red-900">
            <p className="text-gray-400 text-sm mb-1">Outdated Devices</p>
            <p className="text-3xl font-bold text-red-400">{summary?.outdated_devices}</p>
            <p className="text-gray-500 text-xs mt-2">Need attention</p>
          </div>

          {/* Up to date devices card */}
          <div
          onClick={() => navigate("/compliance/up_to_date")}
          className="bg-gray-900 rounded-xl p-6 border border-green-900">
            <p className="text-gray-400 text-sm mb-1">Up to Date Devices</p>
            <p className="text-3xl font-bold text-green-400">{summary?.uptodate_devices}</p>
            <p className="text-gray-500 text-xs mt-2">Fully compliant</p>
          </div>

          {/* Legacy devices card */}
          <div
          onClick={() => navigate("/compliance/legacy")}
          className="bg-gray-900 rounded-xl p-6 border border-gray-700">
            <p className="text-gray-400 text-sm mb-1">Legacy Devices</p>
            <p className="text-3xl font-bold text-gray-400">{summary?.legacy_devices || 0}</p>
            <p className="text-gray-500 text-xs mt-2">Excluded from compliance</p>
          </div>

        </div>

        {/* Driver status chart */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-8">
          <h2 className="text-white font-semibold mb-4">Driver Status Breakdown</h2>
          <div className="flex gap-4">

            {/* Visual bar showing driver status proportions */}
            <div className="flex-1">
              <div className="flex rounded-full overflow-hidden h-6 mb-3 cursor-pointer">
                <div
                  className="bg-green-500 transition-all hover:opacity-80"
                  style={{ width: `${summary?.total_drivers ? (summary.up_to_date / summary.total_drivers) * 100 : 0}%` }}
                  onClick={() => navigate("/compliance/up_to_date")}
                  title="Click to view compliant devices"
                />
                <div
                  className="bg-yellow-500 transition-all hover:opacity-80"
                  style={{ width: `${summary?.total_drivers ? ((summary.aging || 0) / summary.total_drivers) * 100 : 0}%` }}
                  onClick={() => navigate("/compliance/up_to_date")}
                  title="Click to view aging devices"
                />
                <div
                  className="bg-blue-500 transition-all hover:opacity-80"
                  style={{ width: `${summary?.total_drivers ? ((summary.stable || 0) / summary.total_drivers) * 100 : 0}%` }}
                  onClick={() => navigate("/compliance/up_to_date")}
                  title="Click to view stable devices"
                />
                <div
                  className="bg-red-500 transition-all hover:opacity-80"
                  style={{ width: `${summary?.total_drivers ? (summary.outdated / summary.total_drivers) * 100 : 0}%` }}
                  onClick={() => navigate("/compliance/outdated")}
                  title="Click to view outdated devices"
                />
                <div
                  className="bg-gray-600 transition-all hover:opacity-80"
                  style={{ width: `${summary?.total_drivers ? (summary.unknown / summary.total_drivers) * 100 : 0}%` }}
                  onClick={() => navigate("/compliance/unknown")}
                  title="Click to view unknown devices"
                />
              </div>

              {/* Legend */}
              <div className="flex gap-6 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-400">Up to date ({summary?.up_to_date})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-gray-400">Aging ({summary?.aging || 0})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-gray-400">Stable ({summary?.stable || 0})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-gray-400">Outdated ({summary?.outdated})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-600"></div>
                    <span className="text-gray-400">Unknown ({summary?.unknown})</span>
                  </div>
                </div>
            </div>

          </div>
        </div>

        {/* Device list */}
        {/* Device list */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-semibold">Devices</h2>
            <div className="flex gap-3">
              {/* Delete Selected button - only active when devices are selected */}
              <button
                onClick={() => {
                  if (selectedDevices.length === 0) return;
                  if (window.confirm(`Delete ${selectedDevices.length} device(s)? This will also delete all their driver records.`)) {
                    Promise.all(
                      selectedDevices.map(id =>
                        axios.delete(`${API_URL}/devices/${id}`, {
                          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
                        })
                      )
                    ).then(() => {
                      setSelectedDevices([]);
                      axios
                        .get(`${API_URL}/organizations/${ORG_ID}/devices`, {
                          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                        })
                        .then((res) => setDashboardDevices(res.data));
                    });
                  }
                }}
                disabled={selectedDevices.length === 0 || isSuspended}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  selectedDevices.length > 0 && !isSuspended
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
              >
                Delete Selected {selectedDevices.length > 0 && `(${selectedDevices.length})`}
              </button>

              {/* Deploy to Selected button - only active when devices are selected */}
              <button
                onClick={() => {
                  if (selectedDevices.length === 0) return;
                  setShowDeployModal(true);
                }}
                disabled={selectedDevices.length === 0 || isSuspended}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  selectedDevices.length > 0 && !isSuspended
                    ? "bg-blue-600 hover:bg-blue-500 text-white"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
              >
                Deploy to Selected {selectedDevices.length > 0 && `(${selectedDevices.length})`}
              </button>

              {/* Save as Group button - only active when devices are selected */}
              <button
                onClick={() => {
                  if (selectedDevices.length === 0) return;
                  setShowSaveGroupModal(true);
                }}
                disabled={selectedDevices.length === 0 || isSuspended}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  selectedDevices.length > 0 && !isSuspended
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
              >
                Save as Group {selectedDevices.length > 0 && `(${selectedDevices.length})`}
              </button>

              {/* Export CSV button - always available */}
              <button
                onClick={() => {
                  const headers = ["Hostname", "OS", "OS Version", "Last Seen", "Compliance", "Compliance %", "Total Drivers"];
                  const rows = dashboardDevices.map(d => [
                    d.hostname,
                    d.os,
                    d.os_version,
                    new Date(d.last_seen).toLocaleString(),
                    d.compliant ? "Compliant" : d.total_drivers > 0 ? "Non-Compliant" : "No Data",
                    d.compliance_pct || 0,
                    d.total_drivers || 0
                  ]);
                  const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  const now = new Date();
                  const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}-${String(now.getMinutes()).padStart(2,'0')}-${String(now.getSeconds()).padStart(2,'0')}`;
                  a.href = url;
                  a.download = `devices_${timestamp}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
              >
                Export CSV
              </button>
            </div>
          </div>
          {/* Search bar */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by hostname or serial number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500 placeholder-gray-500"
            />
          </div>

          <DeviceList 
            devices={dashboardDevices.filter(d => {
              if (!searchQuery) return true;
              const q = searchQuery.toLowerCase();
              return (
                d.hostname?.toLowerCase().includes(q) ||
                d.serial_number?.toLowerCase().includes(q)
              );
            })}
            referenceDevices={referenceDevices}
            selectedDevices={selectedDevices}
            setSelectedDevices={setSelectedDevices}
            onDeleteSuccess={() => {
              // Refresh devices after deletion
              axios
                .get(`${API_URL}/organizations/${ORG_ID}/devices`, {
                  headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
                })
                .then((res) => setDashboardDevices(res.data));
            }}
          />
        </div>
          {showDeployModal && (
          <DeployToGroupModal
            deviceCount={selectedDevices.length}
            packages={twPackages}
            onConfirm={async (packageId, scheduledAt, groupName) => {
              try {
                let targetType = "single_device";
                let targetGroupId = null;

                if (groupName) {
                  // Create the group first, then deploy to it
                  const groupRes = await axios.post(
                    `${API_URL}/organizations/${ORG_ID}/tw-device-groups`,
                    { name: groupName, device_ids: selectedDevices },
                    { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                  );
                  targetType = "device_group";
                  targetGroupId = groupRes.data.id;
                }

                if (targetType === "device_group") {
                  const params = new URLSearchParams({
                    package_id: packageId,
                    target_type: "device_group",
                    target_group_id: targetGroupId
                  });
                  if (scheduledAt) params.append("scheduled_at", scheduledAt);
                  await axios.post(
                    `${API_URL}/organizations/${ORG_ID}/tw-deployments?${params.toString()}`,
                    {},
                    { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                  );
                } else {
                  // No group requested — deploy individually to each selected device
                  await Promise.all(selectedDevices.map(deviceId => {
                    const params = new URLSearchParams({
                      package_id: packageId,
                      target_type: "single_device",
                      target_device_id: deviceId
                    });
                    if (scheduledAt) params.append("scheduled_at", scheduledAt);
                    return axios.post(
                      `${API_URL}/organizations/${ORG_ID}/tw-deployments?${params.toString()}`,
                      {},
                      { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                    );
                  }));
                }

                setShowDeployModal(false);
                setSelectedDevices([]);
                alert(`✅ Deployment ${scheduledAt ? "scheduled" : "queued"} for ${selectedDevices.length} device(s)${groupName ? ` and saved as group "${groupName}"` : ""}.`);
              } catch (err) {
                alert(err.response?.data?.detail || "❌ Failed to create deployment. Please try again.");
              }
            }}
            onCancel={() => setShowDeployModal(false)}onCancel={() => setShowDeployModal(false)}
          />
        )}

        {showSaveGroupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4">
              <h2 className="text-white font-semibold text-lg mb-1">Save Group</h2>
              <p className="text-gray-400 text-sm mb-6">
                Save these {selectedDevices.length} selected device{selectedDevices.length !== 1 ? "s" : ""} as a reusable group. You can deploy to it later from the Device Groups page.
              </p>
              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-1">Group Name</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  placeholder="e.g. Accounting Team Laptops"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    if (!newGroupName.trim()) { alert("Please enter a group name."); return; }
                    try {
                      await axios.post(
                        `${API_URL}/organizations/${ORG_ID}/tw-device-groups`,
                        { name: newGroupName.trim(), device_ids: selectedDevices },
                        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
                      );
                      setShowSaveGroupModal(false);
                      setNewGroupName("");
                      setSelectedDevices([]);
                      alert(`✅ Group "${newGroupName.trim()}" saved with ${selectedDevices.length} device(s).`);
                    } catch (err) {
                      alert(err.response?.data?.detail || "❌ Failed to save group. Please try again.");
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                >
                  Save Group
                </button>
                <button
                  onClick={() => { setShowSaveGroupModal(false); setNewGroupName(""); }}
                  className="flex-1 bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// This component fetches and displays the list of devices for the organization
function DeviceList({ devices, referenceDevices, selectedDevices, setSelectedDevices, onDeleteSuccess }) {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const [colWidths, setColWidths] = useState(null);

  useEffect(() => {
    if (tableRef.current) {
      const total = tableRef.current.offsetWidth;
      setColWidths([32, total * 0.22, total * 0.22, total * 0.12, total * 0.18, total * 0.14, total * 0.10]);
    }
  }, []);

  function startResize(e, index) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = colWidths ? colWidths[index] : 0;
    function onMouseMove(e) {
      const newWidth = Math.max(60, startWidth + e.clientX - startX);
      setColWidths(prev => {
        const updated = [...prev];
        updated[index] = newWidth;
        return updated;
      });
    }
    function onMouseUp() {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  // Toggle a single device selection
  function toggleDevice(deviceId) {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  }

  // Toggle all devices selected or deselected
  function toggleAll() {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map(d => d.id));
    }
  }

  if (!devices || devices.length === 0) return <p className="text-gray-400">No devices found</p>;

  return (
    <div className="overflow-x-auto" ref={tableRef}>
    <table className="text-sm" style={{ tableLayout: "fixed", width: colWidths ? colWidths.reduce((a, b) => a + b, 0) : "100%" }}>
      <colgroup>
        {colWidths && colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
      </colgroup>
      <thead>
        <tr className="text-gray-400 border-b border-gray-800">
          {/* Select all checkbox */}
          <th className="py-3 relative" style={{ width: colWidths ? colWidths[0] : 32 }}>
            <input
              type="checkbox"
              checked={selectedDevices.length === devices.length && devices.length > 0}
              onChange={toggleAll}
              className="cursor-pointer"
            />
          </th>
          {["Hostname", "Model", "OS", "Last Seen", "Compliance", "Status"].map((label, i) => (
            <th key={label} className="text-left py-3 relative select-none" style={{ width: colWidths ? colWidths[i + 1] : "auto" }}>
              {label}
              <div
                onMouseDown={(e) => startResize(e, i + 1)}
                className="absolute right-0 top-0 h-full w-2 cursor-col-resize flex items-center justify-center group"
              >
                <div className="w-px h-4 bg-gray-600 group-hover:bg-blue-400 transition-colors" />
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {devices.map((device) => {
          return (
            <tr
              key={device.id}
              className="border-b border-gray-800 hover:bg-gray-800 transition-colors cursor-pointer"
            >
              {/* Checkbox - stops click propagation so it doesn't navigate */}
              <td className="py-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedDevices.includes(device.id)}
                  onChange={() => toggleDevice(device.id)}
                  className="cursor-pointer"
                />
              </td>
              <td className="py-3 text-white font-medium" onClick={() => navigate(`/devices/${device.id}`)}>
                <div className="flex items-center gap-2">
                  <DeviceStatusIcon online={device.is_online} />
                  {device.hostname}
                  {referenceDevices.some(r => r.device_id === device.id) && (
                    <span className="bg-blue-900 text-blue-300 text-xs px-2 py-1 rounded-full">Reference</span>
                  )}
                </div>
              </td>
              <td className="py-3 text-gray-400" onClick={() => navigate(`/devices/${device.id}`)}>{device.manufacturer} {device.model}</td>
              <td className="py-3 text-gray-400" onClick={() => navigate(`/devices/${device.id}`)}>{device.os}</td>
              <td className="py-3 text-gray-400" onClick={() => navigate(`/devices/${device.id}`)}>{new Date(device.last_seen).toLocaleString()}</td>
              <td className="py-3" onClick={() => navigate(`/devices/${device.id}`)}>
                {device.is_legacy === "true" ? (
                  <span className="bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded-full">Legacy</span>
                ) : device.total_drivers > 0 ? (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      device.compliant
                        ? "bg-green-900 text-green-300"
                        : device.compliance_pct >= 50
                        ? "bg-yellow-900 text-yellow-300"
                        : "bg-red-900 text-red-300"
                    }`}>
                      {device.compliant ? "Compliant" : "Non-Compliant"}
                    </span>
                    <span className="text-gray-500 text-xs">{device.compliance_pct}%</span>
                  </div>
                ) : (
                  <span className="bg-gray-700 text-gray-400 text-xs px-2 py-1 rounded-full">No Data</span>
                )}
              </td>
              <td className="py-3" onClick={() => navigate(`/devices/${device.id}`)}>
                <span className="bg-blue-900 text-blue-300 text-xs px-2 py-1 rounded-full">Active</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}